import React, { useState, useEffect } from 'react';
import { produkHukumService } from '../../../services/produkHukumService';
import { useAuth } from '../../Admin/context/AuthContext';   // ✅ sesuaikan path
import {
    FaPlus, FaEdit, FaTrash, FaSearch, FaFilePdf, FaTimes,
    FaSave, FaCheckCircle, FaExclamationTriangle, FaEye,
    FaDownload, FaFilter, FaChevronLeft, FaChevronRight
} from 'react-icons/fa';

const kategoriOptions = [
    { value: 'perda', label: 'Peraturan Daerah' },
    { value: 'perwali', label: 'Peraturan Walikota' },
    { value: 'kepwal', label: 'Keputusan Walikota' },
    { value: 'kepkadinas', label: 'Keputusan Kepala Dinas' },
    { value: 'se', label: 'Surat Edaran (SE)' },
    { value: 'lainya', label: 'Lainnya' },
];

const statusOptions = [
    { value: 'Berlaku', label: 'Berlaku' },
    { value: 'Dicabut', label: 'Dicabut' },
    { value: 'Diubah', label: 'Diubah' },
];

const ManajemenProdukHukum = () => {
    const [dataList, setDataList] = useState([]);
    const [statistics, setStatistics] = useState({});
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [notification, setNotification] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterKategori, setFilterKategori] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [fileUpload, setFileUpload] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // ✅ Ambil user dari AuthContext
    const { user } = useAuth();

    // ✅ Helper cek permission
    const hasPermission = (permission) => {
        if (!user) return false;
        if (user?.roles?.includes('admin')) return true;
        if (!user?.permissions) return false;
        return user.permissions.includes(permission);
    };

    const [formData, setFormData] = useState({
        judul: '',
        nomor: '',
        kategori: 'perda',
        deskripsi: '',
        tanggal: new Date().toISOString().split('T')[0],
        status: 'Berlaku',
        order: 0,
        publish_status: 'publish',
    });

    const loadData = async (page = 1) => {
        setLoading(true);
        const params = { page };
        if (searchTerm) params.search = searchTerm;
        if (filterKategori) params.kategori = filterKategori;
        if (filterStatus) params.publish_status = filterStatus;

        const result = await produkHukumService.getList(params);
        if (result.success) {
            setDataList(result.data.data || []);
            setTotalPages(result.data.last_page || 1);
            setCurrentPage(result.data.current_page || 1);
        }
        setLoading(false);
    };

    const loadStatistics = async () => {
        const result = await produkHukumService.getStatistics();
        if (result.success) setStatistics(result.data);
    };

    useEffect(() => {
        loadData(1);
        loadStatistics();
    }, []);

    const showNotif = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    const handleCreate = () => {
        setEditing(null);
        setFormData({
            judul: '',
            nomor: '',
            kategori: 'perda',
            deskripsi: '',
            tanggal: new Date().toISOString().split('T')[0],
            status: 'Berlaku',
            order: 0,
            publish_status: 'publish',
        });
        setFileUpload(null);
        setShowForm(true);
    };

    const handleEdit = (item) => {
        setEditing(item);
        setFormData({
            judul: item.judul || '',
            nomor: item.nomor || '',
            kategori: item.kategori || 'perda',
            deskripsi: item.deskripsi || '',
            tanggal: item.tanggal ? item.tanggal.split('T')[0] : '',
            status: item.status || 'Berlaku',
            order: item.order || 0,
            publish_status: item.publish_status || 'publish',
        });
        setFileUpload(null);
        setShowForm(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        setFileUpload(e.target.files[0]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!editing && !fileUpload) {
            showNotif('File PDF wajib diupload', 'error');
            return;
        }

        setSubmitting(true);

        const submitData = new FormData();
        Object.keys(formData).forEach(key => {
            submitData.append(key, formData[key]);
        });
        if (fileUpload) submitData.append('file', fileUpload);

        let result;
        if (editing) {
            result = await produkHukumService.update(editing.id, submitData);
        } else {
            result = await produkHukumService.create(submitData);
        }

        if (result.success) {
            showNotif(result.message || 'Data berhasil disimpan');
            setShowForm(false);
            loadData(currentPage);
            loadStatistics();
        } else {
            showNotif(result.message, 'error');
        }
        setSubmitting(false);
    };

    const handleDelete = async () => {
        if (!deleteConfirm) return;
        const result = await produkHukumService.delete(deleteConfirm.id);
        if (result.success) {
            showNotif('Data berhasil dihapus');
            loadData(currentPage);
            loadStatistics();
        } else {
            showNotif(result.message, 'error');
        }
        setDeleteConfirm(null);
    };

    const getFileUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/${path}`;
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '-';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    };

    const getKategoriLabel = (val) => {
        return kategoriOptions.find(o => o.value === val)?.label || val;
    };

    return (
        <div>
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-[#0F4C3A]">Manajemen Produk Hukum</h2>
                    <p className="text-gray-600">Kelola dokumen produk hukum (PDF)</p>
                </div>
                {/* ✅ Tombol Tambah — cek permission */}
                {hasPermission('produk-hukum.create') && (
                    <button onClick={handleCreate} className="btn-primary flex items-center justify-center">
                        <FaPlus className="mr-2" /> Tambah Produk Hukum
                    </button>
                )}
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                {[
                    { label: 'Total', value: statistics.total, color: 'text-[#0F4C3A]' },
                    { label: 'Publish', value: statistics.publish, color: 'text-green-600' },
                    { label: 'Draft', value: statistics.draft, color: 'text-yellow-600' },
                    { label: 'Arsip', value: statistics.arsip, color: 'text-gray-500' },
                    { label: 'Views', value: statistics.total_views, color: 'text-blue-600' },
                    { label: 'Downloads', value: statistics.total_downloads, color: 'text-purple-600' },
                ].map((stat, i) => (
                    <div key={i} className="bg-white rounded-lg p-3 shadow-sm text-center">
                        <p className={`text-xl font-bold ${stat.color}`}>{stat.value || 0}</p>
                        <p className="text-xs text-gray-500">{stat.label}</p>
                    </div>
                ))}
            </div>

            {notification && (
                <div className={`mb-4 p-4 rounded-lg flex items-center ${notification.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
                    }`}>
                    {notification.type === 'success' ? <FaCheckCircle className="mr-2" /> : <FaExclamationTriangle className="mr-2" />}
                    {notification.message}
                </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-xl p-4 shadow-sm mb-6 flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Cari judul, nomor, atau deskripsi..."
                        className="w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                    />
                </div>
                <select
                    value={filterKategori}
                    onChange={(e) => setFilterKategori(e.target.value)}
                    className="px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                >
                    <option value="">Semua Kategori</option>
                    {kategoriOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                >
                    <option value="">Semua Status</option>
                    <option value="publish">Publish</option>
                    <option value="draft">Draft</option>
                    <option value="arsip">Arsip</option>
                </select>
                <button onClick={() => loadData(1)} className="btn-primary">
                    <FaFilter className="inline mr-1" /> Filter
                </button>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr className="text-left text-sm text-gray-500">
                                <th className="p-4">No</th>
                                <th className="p-4">Judul / Nomor</th>
                                <th className="p-4">Kategori</th>
                                <th className="p-4">Tanggal</th>
                                <th className="p-4">File</th>
                                <th className="p-4">Status</th>
                                <th className="p-4 text-center">Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="p-8 text-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2E7D32] mx-auto"></div>
                                    </td>
                                </tr>
                            ) : dataList.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="p-8 text-center text-gray-500">
                                        Belum ada data produk hukum
                                    </td>
                                </tr>
                            ) : (
                                dataList.map((item, index) => (
                                    <tr key={item.id} className="border-b hover:bg-gray-50">
                                        <td className="p-4 text-gray-500">
                                            {(currentPage - 1) * 15 + index + 1}
                                        </td>
                                        <td className="p-4">
                                            <p className="font-semibold text-[#0F4C3A]">{item.judul}</p>
                                            {item.nomor && (
                                                <p className="text-xs text-gray-500">No. {item.nomor}</p>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <span className="bg-blue-50 text-blue-600 text-xs px-2 py-1 rounded-full">
                                                {getKategoriLabel(item.kategori)}
                                            </span>
                                        </td>
                                        <td className="p-4 text-sm text-gray-600">
                                            {formatDate(item.tanggal)}
                                        </td>
                                        <td className="p-4">
                                            <a
                                                href={getFileUrl(item.file_path)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-1 text-red-500 hover:text-red-700 text-sm"
                                            >
                                                <FaFilePdf />
                                                <span className="text-xs">{formatFileSize(item.file_size)}</span>
                                            </a>
                                        </td>
                                        <td className="p-4">
                                            <span className={`text-xs px-2 py-1 rounded-full ${item.publish_status === 'publish' ? 'bg-green-100 text-green-700' :
                                                item.publish_status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
                                                    'bg-gray-100 text-gray-500'
                                                }`}>
                                                {item.publish_status}
                                            </span>
                                            <span className={`ml-2 text-xs px-2 py-1 rounded-full ${item.status === 'Berlaku' ? 'bg-green-50 text-green-600' :
                                                item.status === 'Dicabut' ? 'bg-red-50 text-red-600' :
                                                    'bg-yellow-50 text-yellow-600'
                                                }`}>
                                                {item.status}
                                            </span>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex justify-center gap-2">
                                                {/* ✅ Tombol Edit — cek permission */}
                                                {hasPermission('produk-hukum.update') && (
                                                    <button
                                                        onClick={() => handleEdit(item)}
                                                        className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"
                                                        title="Edit"
                                                    >
                                                        <FaEdit />
                                                    </button>
                                                )}

                                                {/* ✅ Tombol Hapus — cek permission */}
                                                {hasPermission('produk-hukum.delete') && (
                                                    <button
                                                        onClick={() => setDeleteConfirm(item)}
                                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                                        title="Hapus"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                )}

                                                {/* Fallback */}
                                                {!hasPermission('produk-hukum.update') && !hasPermission('produk-hukum.delete') && (
                                                    <span className="text-xs text-gray-400 italic">-</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t">
                        <p className="text-sm text-gray-500">Halaman {currentPage} dari {totalPages}</p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => loadData(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                            >
                                <FaChevronLeft />
                            </button>
                            <button
                                onClick={() => loadData(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                            >
                                <FaChevronRight />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Form Modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl max-w-2xl w-full my-8 max-h-[calc(100vh-4rem)] flex flex-col">
                        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white rounded-t-2xl z-10">
                            <h3 className="text-xl font-bold text-[#0F4C3A]">
                                {editing ? 'Edit Produk Hukum' : 'Tambah Produk Hukum'}
                            </h3>
                            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                                <FaTimes size={20} />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto flex-1">
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Judul <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="judul"
                                        value={formData.judul}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        placeholder="Contoh: Peraturan Daerah tentang APBD"
                                        required
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Nomor</label>
                                        <input
                                            type="text"
                                            name="nomor"
                                            value={formData.nomor}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            placeholder="No. 1 Tahun 2024"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Kategori <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            name="kategori"
                                            value={formData.kategori}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        >
                                            {kategoriOptions.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Deskripsi</label>
                                    <textarea
                                        name="deskripsi"
                                        value={formData.deskripsi}
                                        onChange={handleInputChange}
                                        rows="3"
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        placeholder="Deskripsi singkat tentang produk hukum ini..."
                                    />
                                </div>

                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Tanggal</label>
                                        <input
                                            type="date"
                                            name="tanggal"
                                            value={formData.tanggal}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                        <select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        >
                                            {statusOptions.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Urutan</label>
                                        <input
                                            type="number"
                                            name="order"
                                            value={formData.order}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            min="0"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Upload File PDF {!editing && <span className="text-red-500">*</span>}
                                    </label>
                                    <input
                                        type="file"
                                        accept="application/pdf"
                                        onChange={handleFileChange}
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Format: PDF. Maks: 20MB</p>

                                    {editing?.file_name && !fileUpload && (
                                        <p className="mt-2 text-sm text-gray-600">
                                            File saat ini: <span className="font-medium">{editing.file_name}</span>
                                        </p>
                                    )}

                                    {fileUpload && (
                                        <p className="mt-2 text-sm text-green-600">
                                            ✓ {fileUpload.name} ({(fileUpload.size / 1024).toFixed(1)} KB)
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Publish Status</label>
                                    <select
                                        name="publish_status"
                                        value={formData.publish_status}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                    >
                                        <option value="publish">Publish (Tampil di Website)</option>
                                        <option value="draft">Draft (Simpan Saja)</option>
                                        <option value="arsip">Arsip</option>
                                    </select>
                                </div>

                                <div className="flex justify-end gap-3 pt-4 border-t sticky bottom-0 bg-white">
                                    <button
                                        type="button"
                                        onClick={() => setShowForm(false)}
                                        className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                    >
                                        Batal
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="flex items-center bg-gradient-to-r from-[#0F4C3A] to-[#2E7D32] text-white px-6 py-2.5 rounded-lg font-semibold hover:shadow-lg disabled:opacity-50"
                                    >
                                        {submitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                                Mengupload...
                                            </>
                                        ) : (
                                            <>
                                                <FaSave className="mr-2" />
                                                {editing ? 'Update' : 'Simpan'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirm */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FaTrash className="text-3xl text-red-500" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">Hapus Produk Hukum?</h3>
                        <p className="text-gray-600 mb-6">
                            Anda akan menghapus <strong>{deleteConfirm.judul}</strong>.
                        </p>
                        <div className="flex justify-center gap-3">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                className="px-6 py-2.5 border rounded-lg text-gray-700 hover:bg-gray-50"
                            >
                                Batal
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-6 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                                Hapus
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManajemenProdukHukum;